<template>
  <div
    class="h-screen p-8 gap-16 flex flex-col overflow-y-auto"
    :style="{
      background: `linear-gradient(167.65deg, white 60%, #fff1dd 100%)`
    }"
  >
    <div
      class="gap-4 flex items-center text-xl cursor-pointer"
      @click="handleGoBack"
    >
      <BaseIcon icon="angle-left" />
      <span>{{ $t('hub.paymentHistory.back') }}</span>
    </div>

    <div class="gap-8 flex flex-col">
      <span class="text-3xl/10 font-bold">
        {{ $t('hub.paymentHistory.title') }}
      </span>

      <span class="text-lg">
        {{ $t('hub.paymentHistory.description') }}
      </span>
    </div>

    <div class="flex flex-col gap-6">
      <div
        v-for="payment of normalizedPayments"
        :key="payment._id"
        class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm flex flex-col gap-3 text-sm sm:text-base"
      >
        <div class="flex justify-between items-center">
          <div class="flex items-center gap-2 text-gray-800 font-medium">
            <BaseIcon icon="calendar" class="text-primary text-sm" />
            {{ formatDate(payment.createdAt) }}
          </div>
          <div class="text-gray-500 font-semibold whitespace-nowrap">
            {{ payment.amount }} {{ payment.currency }}
          </div>
        </div>

        <div class="flex flex-wrap gap-2 items-center text-xs sm:text-sm">
          <span
            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium"
            :class="getStatusClass(payment.status)"
          >
            <BaseIcon :icon="getStatusIcon(payment.status)" class="text-xs" />

            {{ getStatusLabel(payment.status) }}
          </span>

          <span
            v-if="payment.paymentId"
            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-capino-light text-gray-700 font-medium"
          >
            <BaseIcon icon="receipt" class="text-xs" />
            <span class="select-text">{{
              $t('hub.paymentHistory.orderNumber', {
                paymentId: payment.paymentId
              })
            }}</span>
          </span>
        </div>

        <div>
          <button
            class="underline text-left text-sm mt-1 text-primary hover:text-primary-dark"
            @click="handleOpenRefundModal(payment)"
          >
            {{ $t('hub.paymentHistory.refund.request') }}
          </button>
        </div>
      </div>
    </div>

    <PaymentHistoryRefundModal
      v-if="refundModal.isOpen"
      :payment="refundModal.payment"
      @close="refundModal.isOpen = false"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'

import PaymentHistoryRefundModal from '@/components/Hub/PaymentHistory/PaymentHistoryRefundModal.vue'

export default {
  name: 'HubPaymentHistory',

  components: { PaymentHistoryRefundModal },

  data: () => ({
    refundModal: {
      isOpen: false,
      payment: {}
    }
  }),

  computed: {
    ...mapState('user', ['user']),

    normalizedPayments() {
      return this.user.payments
        .slice()
        .sort((a, b) => b.createdAt - a.createdAt)
        .filter(
          (payment, index, self) =>
            index === self.findIndex(t => t.orderId === payment.orderId)
        )
    }
  },

  methods: {
    handleGoBack() {
      const isCanGoBack = window.history.length > 1
      isCanGoBack ? this.$router.back() : this.$router.push({ name: 'hub' })
    },

    formatDate(timestamp) {
      return new Date(timestamp).toLocaleDateString('uk-UA')
    },

    isFailedPayment(payment) {
      return payment.status === 'error' || payment.status === 'failure'
    },

    getStatusClass(status) {
      const classes = {
        success: 'bg-green-100 text-green-700',
        error: 'bg-red-100 text-red-700',
        failure: 'bg-red-100 text-red-700',
        reversed: 'bg-yellow-100 text-yellow-700',
        default: 'bg-gray-200 text-gray-600'
      }
      return classes[status] || classes.default
    },

    getStatusLabel(status) {
      const labels = {
        success: this.$t('hub.paymentHistory.status.success'),
        error: this.$t('hub.paymentHistory.status.error'),
        failure: this.$t('hub.paymentHistory.status.failure'),
        reversed: this.$t('hub.paymentHistory.status.reversed'),
        default: this.$t('hub.paymentHistory.status.unknown')
      }
      return labels[status] || labels.default
    },

    getStatusIcon(status) {
      const icons = {
        success: 'check-circle',
        error: 'times-circle',
        failure: 'times-circle',
        reversed: 'undo',
        default: 'question-circle'
      }
      return icons[status] || icons.default
    },

    handleOpenRefundModal(payment) {
      this.refundModal.isOpen = true
      this.refundModal.payment = payment
    }
  }
}
</script>
